import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Users as UsersIcon, Check, X, Loader, AlertCircle, CheckCircle, Lock } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AdminPages.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'user' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  const isSuperAdmin = currentUserRole === 'superadmin';

  useMountEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
          setError('Please login to access this page');
          setLoading(false);
          return;
        }

        // Get current user role from localStorage
        const user = JSON.parse(userData);
        setCurrentUserRole(user.role);

        const data = await apiCall('/api/admin/users');
        setUsers(data);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) {
      setError('Please fill in name and email');
      return;
    }

    if (!validateEmail(newUser.email)) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }

    try {
      setLoading(true);
      await apiCall('/api/admin/add-user', {
        method: 'POST',
        body: JSON.stringify(newUser)
      });

      setNewUser({ name: '', email: '', role: 'user' });
      setShowAddForm(false);
      setError('');

      const data = await apiCall('/api/admin/users');
      setUsers(data);
      setSuccess('✓ User added and invitation sent to their email');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.message || 'Error adding user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (userId) => {
    try {
      await apiCall(`/api/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(editData)
      });

      setEditingId(null);
      setEditData({});

      const data = await apiCall('/api/admin/users');
      setUsers(data);
      setSuccess('✓ User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error editing user:', err);
      setError('Error editing user');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      setDeleting(id);
      await apiCall(`/api/admin/users/${id}`, { method: 'DELETE' });
      setUsers(users.filter(u => u._id !== id));
      setConfirmDelete(null);
      setSuccess('✓ User deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error deleting user');
    } finally {
      setDeleting(null);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await apiCall(`/api/admin/users/${changePasswordUser._id}`, {
        method: 'PUT',
        body: JSON.stringify({ password: newPassword })
      });

      setSuccess(`✓ Password changed successfully for ${changePasswordUser.name}`);
      setChangePasswordUser(null);
      setNewPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError('Error changing password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1><UsersIcon size={28} /> User Management</h1>
          <p>Manage system users and permissions</p>
        </div>
        <div className="form-section" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}>
            <Loader size={48} className="spinner" style={{ color: '#3b82f6' }} />
            <p style={{ fontSize: '16px', color: '#64748b', fontWeight: '500' }}>Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><UsersIcon size={28} /> User Management</h1>
          <p>Manage system users and permissions</p>
        </div>
      </div>

      {success && (
        <div className="status-message success">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="close-btn">✕</button>
        </div>
      )}

      {showAddForm && (
        <div className="modal-overlay" onClick={() => !loading && setShowAddForm(false)}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              borderRadius: '12px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '4px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0
                }}>Sending invitation...</p>
              </div>
            </div>
          )}
          <div className="modal-content add-user-modal" onClick={e => e.stopPropagation()} style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <div className="modal-header">
              <h3>➕ Add New User</h3>
              <button
                className="modal-close-btn"
                onClick={() => !loading && setShowAddForm(false)}
                disabled={loading}
                style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
              >
                ✕
              </button>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 20px 0' }}>
              An invitation email will be sent to the user with login credentials
            </p>
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            <div style={{ marginTop: '16px' }}>
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                disabled={loading}
                style={{
                  display: 'block',
                  marginBottom: '12px',
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'auto'
                }}
              />
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  placeholder="Email Address (e.g., user@example.com)"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  disabled={loading}
                  style={{
                    display: 'block',
                    marginBottom: '12px',
                    width: '100%',
                    padding: '10px 12px',
                    paddingRight: '36px',
                    border: newUser.email && !validateEmail(newUser.email) ? '2px solid #dc2626' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? 'not-allowed' : 'auto',
                    background: newUser.email && !validateEmail(newUser.email) ? '#fef2f2' : 'white'
                  }}
                />
                {newUser.email && validateEmail(newUser.email) && (
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '10px',
                    color: '#10b981',
                    fontSize: '18px'
                  }}>
                    ✓
                  </span>
                )}
                {newUser.email && !validateEmail(newUser.email) && (
                  <span style={{
                    position: 'absolute',
                    right: '12px',
                    top: '10px',
                    color: '#dc2626',
                    fontSize: '18px'
                  }}>
                    ✕
                  </span>
                )}
              </div>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                disabled={loading}
                style={{
                  display: 'block',
                  marginBottom: '20px',
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'auto'
                }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddForm(false)}
                disabled={loading}
                style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddUser}
                disabled={loading}
                style={{
                  opacity: loading ? 0.8 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  minHeight: '44px'
                }}
              >
                {loading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                      marginRight: '8px',
                      verticalAlign: 'middle'
                    }}></span>
                    Sending Invitation...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </div>
      )}

      <div className="form-section">
        <div className="section-header">
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
            Users ({users.length})
          </h3>
          {isSuperAdmin && (
            <button className="btn-add-user" onClick={() => setShowAddForm(true)}>
              <Plus size={18} /> Add User
            </button>
          )}
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#dc2626',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 40px', color: '#64748b' }}>
            <Users size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
              {error ? 'Unable to load users' : 'No users found'}
            </p>
            <p style={{ fontSize: '14px' }}>
              {error ? 'Please try again later' : isSuperAdmin ? 'Click "Add User" to create a new user' : 'View user management is available to admins'}
            </p>
          </div>
        ) : (
          <div className="collections-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id}>
                    <td>
                      <strong>{editingId === user._id ? (
                        <input
                          type="text"
                          value={editData.name || user.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', width: '140px' }}
                          placeholder="Name"
                        />
                      ) : (
                        user.name
                      )}</strong>
                    </td>
                    <td>
                      {editingId === user._id ? (
                        <input
                          type="email"
                          value={editData.email || user.email}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', width: '180px' }}
                          placeholder="Email"
                        />
                      ) : (
                        user.email
                      )}
                    </td>
                    <td>
                      {editingId === user._id ? (
                        <select
                          value={editData.role || user.role}
                          onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : (
                        <span className="badge-info" style={{ textTransform: 'capitalize' }}>{user.role}</span>
                      )}
                    </td>
                    <td>
                      {editingId === user._id ? (
                        <select
                          value={editData.isActive !== undefined ? editData.isActive : user.isActive}
                          onChange={(e) => setEditData({ ...editData, isActive: e.target.value === 'true' })}
                          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      ) : (
                        <span className={`status-badge status-${user.isActive ? 'success' : 'error'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {editingId === user._id ? (
                          <>
                            <button
                              className="btn btn-sm"
                              onClick={() => handleEditUser(user._id)}
                              title="Save"
                              style={{ background: '#d1fae5', color: '#065f46', border: 'none' }}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setEditingId(null)}
                              title="Cancel"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            {isSuperAdmin ? (
                              <>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => { setEditingId(user._id); setEditData({}); }}
                                  title="Edit"
                                  style={{ background: '#dbeafe', color: '#0369a1', border: 'none' }}
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => setChangePasswordUser(user)}
                                  title="Change Password"
                                  style={{ background: '#e0e7ff', color: '#4f46e5', border: 'none' }}
                                >
                                  <Lock size={14} />
                                </button>
                                <button
                                  className="btn btn-sm"
                                  onClick={() => setConfirmDelete(user)}
                                  title="Delete"
                                  style={{ background: '#fee2e2', color: '#991b1b', border: 'none' }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            ) : (
                              <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Read Only</span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Delete User</h3>
            <p>Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteUser(confirmDelete._id)}
                disabled={deleting === confirmDelete._id}
              >
                {deleting === confirmDelete._id ? (
                  <>
                    <Loader size={14} className="spinner" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {changePasswordUser && (
        <div className="modal-overlay" onClick={() => setChangePasswordUser(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🔐 Change Password</h3>
            <p>Change password for <strong>{changePasswordUser.name}</strong></p>
            <input
              type="password"
              placeholder="New Password (min 6 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '14px',
                marginTop: '12px',
                marginBottom: '16px'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleChangePassword()}
            />
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setChangePasswordUser(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword ? (
                  <>
                    <Loader size={14} className="spinner" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock size={14} /> Change Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
